import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { Button } from '../components/ui/button';
import { atelierService, apprenantService, actionService, reportingService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { LogOut, Users, CalendarDays, Clock, Plus, Mail, Briefcase, Timer, UserPlus, FolderOpen, GraduationCap, FileDown, CheckCircle2, MessageCircle, Settings, ChevronDown, ClipboardList } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import ResponsiveTabs from '../components/ResponsiveTabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useContratAutoRefresh } from '../hooks/useContratAutoRefresh';
import api from '../services/api';

// Composants extraits (refactoring)
import { AnimateurEspaceView, AnimateurAteliersView, AnimateurBeneficiairesView } from './animateur';
import SuiviDashboard from '../components/SuiviDashboard';

// Composants existants
import DocumentsPartages from '../components/DocumentsPartages';
import EditAtelierDialog from '../components/EditAtelierDialog';
import EmargementDialog from '../components/EmargementDialog';
import BourseEmploi from '../components/BourseEmploi';
import GestionCandidatures from '../components/GestionCandidatures';
import QuestionnaireSatisfaction from '../components/QuestionnaireSatisfaction';
import ValidationDiagnostics from '../components/ValidationDiagnostics';
import NotificationsInApp from '../components/NotificationsInApp';
import ContratsManager from '../components/ContratsManager';
import PeriodesEssaiManager from '../components/PeriodesEssaiManager';
import LiveIndicator from '../components/LiveIndicator';
import Messagerie from '../components/Messagerie';
import MessagerieBadge from '../components/MessagerieBadge';
import EmailComposeModal from '../components/email/EmailComposeModal';

const AnimateurDashboard = () => {
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  
  // États principaux
  const [ateliers, setAteliers] = useState([]);
  const [activites, setActivites] = useState([]);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedBeneficiaire, setSelectedBeneficiaire] = useState(null);
  const [beneficiaireActions, setBeneficiaireActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('espace');
  // Filtre par zone géographique (comme les espaces Admin / Référent)
  const [zone, setZone] = useState('all');
  
  // Modals email
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalDefaults, setEmailModalDefaults] = useState({});

  // États dialogs
  const [editingAtelier, setEditingAtelier] = useState(null);
  const [emargementAtelier, setEmargementAtelier] = useState(null);
  const [pendingEmargements, setPendingEmargements] = useState([]);
  const [showAddActivite, setShowAddActivite] = useState(false);
  const [showInvitation, setShowInvitation] = useState(false);
  const [showActivityReport, setShowActivityReport] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // États formulaires
  const [newActivite, setNewActivite] = useState({
    type: 'preparation', titre: '', date: new Date().toISOString().split('T')[0],
    heure_debut: '09:00', heure_fin: '10:00', notes: '', beneficiaires_ids: []
  });
  const [invitation, setInvitation] = useState({
    beneficiaire_id: '', titre: '', date: '', heure_debut: '', heure_fin: '', notes: '', type_rdv: 'suivi_post_embauche'
  });
  const [reportDateRange, setReportDateRange] = useState({
    date_debut: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    date_fin: new Date().toISOString().split('T')[0]
  });
  
  // Stats temps
  const [timeStats, setTimeStats] = useState({ totalMinutes: 0, activitiesCount: 0, timeByType: {} });

  // Auto-refresh contrats
  const handleContratUpdate = useCallback(() => {}, []);
  const { isConnected, isPolling, lastPollTime } = useContratAutoRefresh(handleContratUpdate, 30000, activeTab === 'espace');

  // Chargement initial
  useEffect(() => { loadData(); }, []);

  // Charger données bénéficiaire
  useEffect(() => {
    if (selectedBeneficiaire) loadBeneficiaireData(selectedBeneficiaire.id);
  }, [selectedBeneficiaire]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Utiliser Promise.allSettled pour que les données se chargent même si un appel échoue
      const results = await Promise.allSettled([
        atelierService.getAll(),
        api.get('/activites-intervenant'),
        apprenantService.getAll(),
        api.get('/animateurs')  // Endpoint accessible aux animateurs
      ]);
      
      // Extraire les données des résultats réussis
      const ateliersRes = results[0].status === 'fulfilled' ? results[0].value : { data: [] };
      const activitesRes = results[1].status === 'fulfilled' ? results[1].value : { data: [] };
      const beneficiairesRes = results[2].status === 'fulfilled' ? results[2].value : { data: [] };
      const usersRes = results[3].status === 'fulfilled' ? results[3].value : { data: [] };
      
      // Log des erreurs pour debug
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['ateliers', 'activites-intervenant', 'apprenants', 'animateurs'];
          console.warn(`[AnimateurDashboard] Erreur chargement ${endpoints[index]}:`, result.reason?.message);
        }
      });
      
      setAteliers(ateliersRes.data || []);
      setActivites(activitesRes.data || []);
      setBeneficiaires(beneficiairesRes.data || []);
      setUsers(usersRes.data || []);
      
      // Calculer stats temps
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      const activitesData = activitesRes.data || [];
      // Vue partagée : l'API renvoie les activités de TOUS les intervenants,
      // mais les statistiques personnelles ne comptent que celles de l'utilisateur connecté
      const mesActivites = activitesData.filter(a => a.intervenant_id === user?.id);
      const monthActivities = mesActivites.filter(a => {
        const actDate = new Date(a.date);
        return actDate.getMonth() === currentMonth && actDate.getFullYear() === currentYear;
      });

      // Calcul de la durée depuis heure_debut / heure_fin si duree_minutes est absent
      const calcDureeMinutes = (act) => {
        if (act.duree_minutes) return act.duree_minutes;
        if (act.heure_debut && act.heure_fin) {
          const [hd, md] = act.heure_debut.split(':').map(Number);
          const [hf, mf] = act.heure_fin.split(':').map(Number);
          const diff = (hf * 60 + mf) - (hd * 60 + md);
          return diff > 0 ? diff : 0;
        }
        return 0;
      };

      let totalMins = 0;
      const byType = {};
      monthActivities.forEach(act => {
        const mins = calcDureeMinutes(act);
        totalMins += mins;
        const type = act.type || 'autre';
        byType[type] = (byType[type] || 0) + mins;
      });
      
      setTimeStats({ totalMinutes: totalMins, activitiesCount: monthActivities.length, timeByType: byType });
      
      // Émargements en attente
      const ateliersData = ateliersRes.data || [];
      // Émargements en attente : tous les ateliers concernés — tout intervenant
      // peut émarger (les intervenants se remplacent mutuellement)
      const pending = ateliersData.filter(a => {
        const atelierDate = new Date(a.date);
        const isPast = atelierDate < now || (atelierDate.toDateString() === now.toDateString());
        const hasUndefinedStatus = a.participants?.some(p => !p.statut_presence || p.statut_presence === 'non_defini');
        const notSigned = !a.signature_animateur;
        return isPast && (hasUndefinedStatus || notSigned);
      });
      setPendingEmargements(pending);
      
    } catch (error) {
      console.error('[AnimateurDashboard] Erreur générale:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadBeneficiaireData = async (beneficiaireId) => {
    try {
      const res = await actionService.getAll(beneficiaireId);
      setBeneficiaireActions(res.data);
    } catch (error) {
      toast.error('Erreur de chargement des actions');
    }
  };

  const handleGeneratePDF = async () => {
    if (!selectedBeneficiaire) return;
    try {
      const now = new Date();
      const response = await reportingService.getSyntheseIndividuelle(selectedBeneficiaire.id, now.getMonth() + 1, now.getFullYear());
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('PDF généré');
    } catch (error) {
      toast.error('Erreur de génération');
    }
  };

  const handleAddActivite = async () => {
    if (!newActivite.titre || !newActivite.date) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    try {
      await api.post('/activites-intervenant', newActivite);
      toast.success('Activité ajoutée');
      setShowAddActivite(false);
      setNewActivite({ type: 'preparation', titre: '', date: new Date().toISOString().split('T')[0], heure_debut: '09:00', heure_fin: '10:00', notes: '', beneficiaires_ids: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSendInvitation = async () => {
    if (!invitation.beneficiaire_id || !invitation.titre || !invitation.date) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    try {
      await api.post('/invitations-rdv', invitation);
      toast.success('Invitation envoyée');
      setShowInvitation(false);
      setInvitation({ beneficiaire_id: '', titre: '', date: '', heure_debut: '', heure_fin: '', notes: '', type_rdv: 'suivi_post_embauche' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadActivityReport = async () => {
    setGeneratingReport(true);
    try {
      const response = await api.get(`/reporting/rapport-activite-intervenant?date_debut=${reportDateRange.date_debut}&date_fin=${reportDateRange.date_fin}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_activite_${reportDateRange.date_debut}_${reportDateRange.date_fin}.pdf`;
      link.click();
      toast.success('Rapport téléchargé');
      setShowActivityReport(false);
    } catch (error) {
      toast.error('Erreur de génération du rapport');
    } finally {
      setGeneratingReport(false);
    }
  };

  const getActivityTypeLabel = (type) => {
    const labels = {
      preparation: 'Préparation', demarchage: 'Démarchage', negociation: 'Négociation',
      administratif: 'Administratif', autre: 'Autre'
    };
    return labels[type] || type;
  };

  // Zones disponibles déduites des bénéficiaires (évite tout codage en dur erroné)
  const availableZones = Array.from(
    new Set(beneficiaires.map((b) => b.zone_geographique).filter(Boolean))
  ).sort();

  // Bénéficiaires filtrés par zone
  const beneficiairesFiltres = zone === 'all'
    ? beneficiaires
    : beneficiaires.filter((b) => b.zone_geographique === zone);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Espace Intervenant
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate">{user?.prenom} {user?.nom}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <div className="hidden sm:block">
              <LiveIndicator isConnected={isConnected} isPolling={isPolling} lastPollTime={lastPollTime} />
            </div>

            {/* Sélecteur de zone géographique */}
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger className="w-32 sm:w-44 h-9 rounded-xl" data-testid="animateur-zone-select">
                <SelectValue placeholder="Toutes les zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les zones</SelectItem>
                {availableZones.map((z) => (
                  <SelectItem key={z} value={z}>{z}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <NotificationsInApp />

            {/* Menu "Plus" pour les onglets secondaires */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-xl h-9 px-2 sm:px-3" data-testid="animateur-more-menu">
                  <Settings className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Plus</span>
                  <ChevronDown className="w-4 h-4 ml-1 hidden sm:inline" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Tâches ponctuelles</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setActiveTab('periodes-essai')}
                  className={activeTab === 'periodes-essai' ? 'bg-blue-50 text-blue-700' : ''}
                >
                  <Timer className="w-4 h-4 mr-2" />
                  Périodes d'Essai
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveTab('documents')}
                  className={activeTab === 'documents' ? 'bg-blue-50 text-blue-700' : ''}
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Documents
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setActiveTab('satisfaction')}
                  className={activeTab === 'satisfaction' ? 'bg-blue-50 text-blue-700' : ''}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mon Avis
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Cloche / badge messagerie : compteur de messages non lus */}
            <MessagerieBadge onClick={() => setActiveTab('messagerie')} />

            <Button onClick={logout} variant="outline" className="rounded-xl h-9 px-2 sm:px-3">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto p-4 sm:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <ResponsiveTabs
            asTabsListReplacement
            value={activeTab}
            onValueChange={setActiveTab}
            className="mb-6"
            listClassName="bg-white rounded-2xl p-1 shadow-sm border border-slate-200"
            tabClassName="rounded-xl px-3 lg:px-4 relative data-[state=active]:bg-slate-100"
            tabs={[
              {
                value: 'espace',
                label: 'Mon Espace',
                icon: Briefcase,
                badge: pendingEmargements.length > 0 ? (
                  <Badge className="bg-red-500 text-white text-xs h-5 px-1.5 flex items-center p-0 rounded-full">
                    {pendingEmargements.length}
                  </Badge>
                ) : null,
              },
              { value: 'ateliers', label: 'Ateliers', icon: CalendarDays },
              { value: 'beneficiaires', label: 'Bénéficiaires', icon: Users },
              { value: 'suivi', label: 'Suivi', icon: ClipboardList },
              { value: 'contrats', label: 'Contrats', icon: Briefcase },
              { value: 'messagerie', label: 'Messagerie', icon: MessageCircle },
              { value: 'diagnostics', label: 'Diagnostics', icon: GraduationCap },
              ...(hasPermission('view_candidatures') ? [{ value: 'candidatures', label: 'Candidatures', icon: Users }] : []),
              ...(hasPermission('view_bourse_emploi') ? [{ value: 'bourse', label: 'Bourse Emploi', icon: Briefcase }] : []),
            ]}
          />

          {/* Indicateur vue secondaire active */}
          {['periodes-essai', 'documents', 'satisfaction'].includes(activeTab) && (
            <div className="mb-4">
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                Vue active : {activeTab === 'periodes-essai' ? "Périodes d'Essai" : activeTab === 'documents' ? 'Documents' : 'Mon Avis'}
              </Badge>
            </div>
          )}

          {/* Tab Espace */}
          <TabsContent value="espace">
            <AnimateurEspaceView
              timeStats={timeStats}
              activites={activites}
              ateliers={ateliers}
              users={users}
              currentUserId={user?.id}
              zone={zone === 'all' ? null : zone}
              pendingEmargements={pendingEmargements}
              onShowActivityReport={() => setShowActivityReport(true)}
              onShowAddActivite={() => setShowAddActivite(true)}
              onShowInvitation={() => setShowInvitation(true)}
              onEditAtelier={setEditingAtelier}
              onOpenEmargement={setEmargementAtelier}
              getActivityTypeLabel={getActivityTypeLabel}
            />
          </TabsContent>

          {/* Tab Bourse */}
          <TabsContent value="bourse">
            <BourseEmploi canCreate={hasPermission('manage_bourse_emploi')} canEdit={hasPermission('manage_bourse_emploi')} canDelete={hasPermission('manage_bourse_emploi')} isBeneficiaire={false} />
          </TabsContent>

          {/* Tab Ateliers */}
          <TabsContent value="ateliers">
            <AnimateurAteliersView
              ateliers={ateliers}
              users={users}
              currentUserId={user?.id}
              onEditAtelier={setEditingAtelier}
              onOpenEmargement={setEmargementAtelier}
              onRefresh={loadData}
            />
          </TabsContent>

          {/* Tab Contrats */}
          <TabsContent value="contrats">
            <ContratsManager userRole="animateur" />
          </TabsContent>

          {/* Tab Périodes Essai */}
          <TabsContent value="periodes-essai">
            <PeriodesEssaiManager />
          </TabsContent>

          {/* Tab Bénéficiaires */}
          <TabsContent value="beneficiaires">
            <AnimateurBeneficiairesView
              beneficiaires={beneficiairesFiltres}
              selectedBeneficiaire={selectedBeneficiaire}
              setSelectedBeneficiaire={setSelectedBeneficiaire}
              beneficiaireActions={beneficiaireActions}
              ateliers={ateliers}
              activites={activites}
              onGeneratePDF={handleGeneratePDF}
              onLoadBeneficiaireData={loadBeneficiaireData}
              onRefresh={loadData}
            />
          </TabsContent>

          {/* Tab Suivi (tableau de bord des comptes-rendus) */}
          <TabsContent value="suivi">
            <SuiviDashboard
              zone={zone === 'all' ? null : zone}
              onOpenBeneficiaire={(id) => {
                const ben = beneficiaires.find((b) => b.id === id);
                if (ben) {
                  setSelectedBeneficiaire(ben);
                  setActiveTab('beneficiaires');
                }
              }}
            />
          </TabsContent>

          {/* Tab Diagnostics */}
          <TabsContent value="diagnostics">
            <ValidationDiagnostics />
          </TabsContent>

          {/* Tab Candidatures */}
          <TabsContent value="candidatures">
            <GestionCandidatures />
          </TabsContent>

          {/* Tab Documents */}
          <TabsContent value="documents">
            <DocumentsPartages userRole="animateur" />
          </TabsContent>

          {/* Tab Messagerie */}
          <TabsContent value="messagerie">
            <div>
              <div className="flex justify-end mb-4">
                <Button
                  onClick={() => { setEmailModalDefaults({}); setShowEmailModal(true); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Composer un email
                </Button>
              </div>
              <Messagerie />
            </div>
          </TabsContent>

          {/* Tab Satisfaction */}
          <TabsContent value="satisfaction">
            <QuestionnaireSatisfaction />
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog Ajouter Activité */}
      <Dialog open={showAddActivite} onOpenChange={setShowAddActivite}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>Ajouter une activité</DialogTitle>
            <DialogDescription>Planifiez vos temps de travail</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type d'activité</Label>
              <Select value={newActivite.type} onValueChange={(val) => setNewActivite({ ...newActivite, type: val })}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="preparation">Préparation</SelectItem>
                  <SelectItem value="demarchage">Démarchage entreprise</SelectItem>
                  <SelectItem value="negociation">Négociation</SelectItem>
                  <SelectItem value="administratif">Administratif</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input value={newActivite.titre} onChange={(e) => setNewActivite({ ...newActivite, titre: e.target.value })} placeholder="Description de l'activité" className="rounded-xl" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={newActivite.date} onChange={(e) => setNewActivite({ ...newActivite, date: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Début</Label>
                <Input type="time" value={newActivite.heure_debut} onChange={(e) => setNewActivite({ ...newActivite, heure_debut: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Fin</Label>
                <Input type="time" value={newActivite.heure_fin} onChange={(e) => setNewActivite({ ...newActivite, heure_fin: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={newActivite.notes} onChange={(e) => setNewActivite({ ...newActivite, notes: e.target.value })} placeholder="Notes optionnelles..." className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Bénéficiaires rattachés
                {newActivite.beneficiaires_ids.length > 0 && (
                  <Badge className="bg-blue-100 text-blue-700">{newActivite.beneficiaires_ids.length} sélectionné{newActivite.beneficiaires_ids.length > 1 ? 's' : ''}</Badge>
                )}
              </Label>
              <p className="text-xs text-slate-500 mb-2">Le temps sera divisé entre les bénéficiaires sélectionnés</p>
              <ScrollArea className="h-40 border rounded-xl p-2">
                {beneficiaires.length === 0 ? (
                  <div className="text-center py-8 text-sm text-slate-500">
                    Aucun bénéficiaire à afficher.<br />
                    <span className="text-xs">Ajoutez d'abord un bénéficiaire depuis l'onglet « Bénéficiaires ».</span>
                  </div>
                ) : (
                  beneficiaires.map((ben) => (
                    <div key={ben.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${newActivite.beneficiaires_ids.includes(ben.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'}`}
                      onClick={() => {
                        const isSelected = newActivite.beneficiaires_ids.includes(ben.id);
                        setNewActivite({ ...newActivite, beneficiaires_ids: isSelected ? newActivite.beneficiaires_ids.filter(id => id !== ben.id) : [...newActivite.beneficiaires_ids, ben.id] });
                      }}>
                      <Checkbox checked={newActivite.beneficiaires_ids.includes(ben.id)} className="pointer-events-none" />
                      <span className="font-medium">{ben.prenom} {ben.nom}</span>
                    </div>
                  ))
                )}
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddActivite(false)} className="rounded-xl">Annuler</Button>
            <Button onClick={handleAddActivite} className="bg-blue-600 hover:bg-blue-700 rounded-xl">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Invitation RDV */}
      <Dialog open={showInvitation} onOpenChange={setShowInvitation}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>Inviter un bénéficiaire</DialogTitle>
            <DialogDescription>Envoyez une invitation par email pour un rendez-vous</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bénéficiaire</Label>
              <Select value={invitation.beneficiaire_id || ''} onValueChange={(val) => setInvitation({ ...invitation, beneficiaire_id: val === 'none' ? '' : val })}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {beneficiaires.map((b) => (<SelectItem key={b.id} value={b.id}>{b.prenom} {b.nom}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Type de rendez-vous</Label>
              <Select value={invitation.type_rdv || 'suivi_post_embauche'} onValueChange={(val) => setInvitation({ ...invitation, type_rdv: val })}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="suivi_post_embauche">Suivi post-embauche</SelectItem>
                  <SelectItem value="intervention_individuelle">Intervention individuelle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Objet du rendez-vous</Label>
              <Input value={invitation.titre} onChange={(e) => setInvitation({ ...invitation, titre: e.target.value })} placeholder="Ex: Entretien de suivi" className="rounded-xl" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={invitation.date} onChange={(e) => setInvitation({ ...invitation, date: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Début</Label>
                <Input type="time" value={invitation.heure_debut} onChange={(e) => setInvitation({ ...invitation, heure_debut: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Fin</Label>
                <Input type="time" value={invitation.heure_fin} onChange={(e) => setInvitation({ ...invitation, heure_fin: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Message (optionnel)</Label>
              <Textarea value={invitation.notes} onChange={(e) => setInvitation({ ...invitation, notes: e.target.value })} placeholder="Message personnalisé..." className="rounded-xl" />
            </div>
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-blue-600 mt-0.5" />
                <p className="text-xs text-blue-700">Un email d'invitation sera automatiquement envoyé au bénéficiaire.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvitation(false)} className="rounded-xl">Annuler</Button>
            <Button onClick={handleSendInvitation} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
              <Mail className="w-4 h-4 mr-2" />Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Rapport d'activité */}
      <Dialog open={showActivityReport} onOpenChange={setShowActivityReport}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileDown className="w-6 h-6 text-blue-600" />
              Télécharger mon rapport d'activité
            </DialogTitle>
            <DialogDescription>Générez un PDF détaillé de vos réalisations.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input type="date" value={reportDateRange.date_debut} onChange={(e) => setReportDateRange({...reportDateRange, date_debut: e.target.value})} className="rounded-xl" data-testid="report-date-debut" />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input type="date" value={reportDateRange.date_fin} onChange={(e) => setReportDateRange({...reportDateRange, date_fin: e.target.value})} className="rounded-xl" data-testid="report-date-fin" />
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <h4 className="font-medium text-blue-900 text-sm mb-2">Le rapport contiendra :</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                {['Synthèse globale', 'Répartition par type', 'Ateliers animés', 'RDV individuels', 'Autres activités'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowActivityReport(false)} className="rounded-xl">Annuler</Button>
            <Button onClick={handleDownloadActivityReport} disabled={generatingReport} className="bg-blue-600 hover:bg-blue-700 rounded-xl" data-testid="generate-report-btn">
              {generatingReport ? <><Clock className="w-4 h-4 mr-2 animate-spin" />Génération...</> : <><FileDown className="w-4 h-4 mr-2" />Télécharger</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogs existants */}
      <EditAtelierDialog atelier={editingAtelier} open={!!editingAtelier} onOpenChange={(open) => !open && setEditingAtelier(null)} onSuccess={loadData} />
      <EmargementDialog open={!!emargementAtelier} onOpenChange={(open) => !open && setEmargementAtelier(null)} event={emargementAtelier} eventType="atelier" onUpdate={loadData} />

      {/* Modal email avec pièces jointes */}
      <EmailComposeModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultTo={emailModalDefaults.to || ''}
        defaultSubject={emailModalDefaults.subject || ''}
        defaultBody={emailModalDefaults.body || ''}
      />
    </div>
  );
};

export default AnimateurDashboard;
